import { Check, ChevronDown, Search } from 'lucide-react'
import {
  type ComponentType,
  type CSSProperties,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Tooltip } from './Tooltip'
import { useAnchoredPopover } from './useAnchoredPopover'
import type { PopoverSide } from './useClampToViewport'
import { useListbox } from './useListbox'
import './select.css'

type IconType = ComponentType<{
  size?: number
  className?: string
  'aria-hidden'?: boolean
}>

/**
 * How many options fit the panel before the list is scrolled rather than read.
 *
 * `--popover-max-h` caps the panel at 18rem, which holds six rows. Past that a
 * value is found by scrolling, and a list you scroll is one you should be able to
 * type at. It is the panel's own capacity rather than a preference — move the cap
 * and this moves with it.
 */
const SCROLLS_PAST = 6

/**
 * What the filter compares — case and punctuation folded away.
 *
 * It reads the hint as well as the label, because in a bilingual list the two
 * are the two scripts of one name: searchable in one of them only, half the
 * list is unreachable by the word somebody knows it as.
 */
const fold = (t: string) => t.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')

/**
 * The app's select — the only one, in a form and in a toolbar alike.
 *
 * It is ours rather than the browser's, so everything the platform gives a
 * native control away for free is ours to supply: typeahead, arrow-key
 * navigation, Home/End, an accessible name, a value a form will submit. `useListbox` carries the keyboard half and the hidden
 * input below carries the form half. Neither is optional — a control that only
 * answers a mouse is broken, not unfinished.
 *
 * The trigger is `role="combobox"`, not a button: `aria-activedescendant` is not
 * supported on role=button, so a button trigger announces the widget and then
 * says nothing about which option the arrow keys are on.
 *
 * **Groups** are a heading and a run of options, and they are presentation only —
 * the keyboard walks the flattened list, so a group boundary never costs a
 * keystroke. They exist because a list mixing zmanim with other davening times is
 * a list you cannot read.
 *
 * **`variant` is appearance, nothing else.** A field fills its grid cell; a tool
 * takes the toolbar family's height and edge. Same component, same behavior.
 */
export type SelectOption<T extends string = string> = {
  value: T
  label: string
  /**
   * A fact about this option, muted at the trailing edge of its row — a price, a
   * size. The MENU only: the trigger shows the label alone, so choosing does not
   * change the control's width.
   *
   * A few characters. It shares the row with the label and gives way before the
   * label does, so anything longer belongs in `description`.
   */
  hint?: string
  /**
   * Words at the same trailing edge — what an answer does, a household's name —
   * set in the interface face. A hint is a figure and takes the figures face; a
   * word in that face reads as a code.
   */
  note?: string
  /**
   * What choosing this option DOES — a sentence, on its own line beneath the
   * label.
   *
   * The distinction from `hint` is length, and it is the label that pays for
   * getting it wrong: a sentence beside the label leaves the label a few
   * characters and an ellipsis, so `Candle lighting` read as `Candle lig…`
   * while the sentence explaining it had the rest of the row. A name must never
   * be the part that is cut.
   *
   * Same word, same meaning, as `Choice`'s own `description`.
   */
  description?: string
  /**
   * A glyph leading the option, for a list whose options are kinds of thing a
   * shape tells apart faster than a word — an envelope, a card. It leads the
   * trigger too once chosen, in place of the trigger's own `icon`, so the
   * closed control says the answer the same way the row did.
   *
   * Every option in the list has one, or none does: a glyph on some answers
   * and not others moves the trigger's words between choices. A `heading`
   * draws none.
   */
  icon?: IconType
  disabled?: boolean
  /**
   * The filter never removes it.
   *
   * For the option that is not a value but a way OUT of the list — "Add an
   * item…". Typing the name of the thing that is not on the list is exactly
   * when somebody needs it, and exactly when a filter over labels would take
   * it away: the panel empties, and the one entry that answers the emptiness
   * goes with it.
   */
  pinned?: boolean
  /**
   * How far down a nested list this sits — 0 at the top, indented one step per
   * level while the list is browsed. The nesting says what an option is under,
   * so a nested list needs no second line to say it.
   */
  depth?: number
  /**
   * Where it sits, said after its name, muted, and only in SEARCH results: a
   * search flattens the list, and what the indent said is then lost. Searched,
   * like the label, so an item is found by the name of what it is under.
   */
  trail?: string
  /**
   * A branch that is not itself a choice — what the options beneath it are of.
   * It is drawn at its depth while the list is browsed, is never an option to
   * the keyboard or a screen reader, and drops out of search results, where
   * each option's `trail` carries it instead.
   */
  heading?: boolean
}
export type SelectGroup<T extends string = string> = {
  label: string
  options: SelectOption<T>[]
}

function flatten<T extends string>(
  items: readonly (SelectOption<T> | SelectGroup<T>)[],
): SelectOption<T>[] {
  return items.flatMap((i) => ('options' in i ? i.options : [i]))
}

/*
 * Generic over the value, so a caller with a union keeps it. The control this
 * replaced was generic and the one before that was not — a plain `string` here
 * pushes a cast into every screen that models its filter as a union, which is
 * most of them.
 */
export function Select<T extends string = string>({
  value,
  onValueChange,
  values,
  onValuesChange,
  multiple = false,
  summary,
  options,
  label,
  placeholder = 'Select',
  icon: Icon,
  searchable = false,
  searchFirst,
  bilingual = false,
  variant = 'field',
  side = 'forward',
  maxHeight,
  disabled = false,
  invalid = false,
  name,
  id,
  'aria-invalid': markedInvalid,
  'aria-describedby': describedBy,
}: {
  value?: T | null
  /** Named for what changed, not for the event — one convention everywhere. */
  onValueChange?: (value: T) => void
  /**
   * Choosing SEVERAL rather than one — the columns an export carries.
   *
   * A prop rather than a second component: a parallel multi-select would be a
   * second floating list with its own dismissal, its own keyboard and its own
   * drift, which is exactly what having one select is for. Picking does not
   * close the panel here, because you are building a set.
   */
  multiple?: boolean
  values?: readonly T[]
  onValuesChange?: (values: T[]) => void
  /** What the trigger reads in `multiple` — "9 chosen". */
  summary?: string
  options: readonly (SelectOption<T> | SelectGroup<T>)[]
  /**
   * Names the control where nothing visible does — a tool in a strip.
   *
   * OMIT IT INSIDE A `Field`. Field renders `<label htmlFor>` against the id it
   * clones in, and a `<button>` is a labelable element, so the visible label is
   * already the accessible name. Setting `aria-label` as well would *override*
   * that name with a second copy, and the two drift — which breaks voice control
   * outright, because the user says the word they can see.
   */
  label?: string
  placeholder?: string
  icon?: IconType
  /**
   * Ask for a filter on a list SHORT enough to go without one — options whose
   * labels are hard to tell apart at a glance. A list long enough to scroll gets
   * one regardless, so this cannot be used to withhold it.
   */
  searchable?: boolean
  /**
   * A list found by TYPING rather than read — a roster of hundreds, where the
   * reader already knows the name. Until something is typed the panel shows
   * this line, the chosen option and the pinned ones, so what is always
   * offered is not buried under everything that is only ever searched for.
   */
  searchFirst?: string
  /**
   * The options pair the two scripts — a column beside its Hebrew heading, a
   * parsha beside its English gloss. Plex Sans Hebrew carries both, so the pair
   * takes one family; left on the UI stack the hint falls to the figures face,
   * which sets Hebrew as monospaced letters with air between them.
   *
   * The picker declares it rather than the option, because whether a list is
   * bilingual is a fact about the list: one option happening to hold no Hebrew
   * must not set it in a different face from the one above it. Same prop, same
   * meaning as `Combobox` — one convention for both pickers.
   */
  bilingual?: boolean
  /** Appearance only — a field fills its grid cell, a tool sits in the strip. */
  variant?: 'field' | 'tool' | 'icon'
  /** Which side of the trigger the panel opens on when that side has room. */
  side?: PopoverSide
  /** A different height cap travels on the portalled panel as its own token. */
  maxHeight?: string
  disabled?: boolean
  invalid?: boolean
  /** Submits with the form, the way a native select does. */
  name?: string
  id?: string
  /**
   * What a `Field` hands the control it holds when its value is refused: that
   * it is invalid, and the id of the sentence saying why. They land on the
   * TRIGGER, which is the element that takes focus and the one a screen reader
   * is on when it reads a description — a wrapper carrying them describes
   * nothing anybody reaches.
   */
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}) {
  const refused = invalid || markedInvalid === true
  const { open, setOpen, ref, panelRef, triggerRef } =
    useAnchoredPopover<HTMLButtonElement>({ side })
  const [query, setQuery] = useState('')
  const listId = useId()
  const optionId = (i: number) => `${listId}-opt-${i}`

  const every = useMemo(() => flatten(options), [options])
  /* The choices; a heading is drawn among them and is never one. */
  const all = useMemo(() => every.filter((o) => !o.heading), [every])
  /*
   * A filter appears once the list is longer than the panel can show, whether or
   * not the caller asked for one. `searchable` opts a SHORTER list in — a set of
   * eight whose labels are hard to tell apart — and cannot opt a long one out:
   * a list you have to scroll is one you should be able to type at, and leaving
   * that to each call site is how the hour picker ended up with twenty-four
   * options and no way to narrow them.
   *
   * The number is the panel's capacity rather than a taste: past it the list is
   * scrolled, and scanning stops being the way anybody finds a value.
   */
  const panelRows = useMemo(
    () =>
      // A group heading takes a row like anything else, so it counts towards what
      // the panel can hold — otherwise a grouped list scrolls while measuring as
      // though it fits.
      every.length + options.filter((i) => 'options' in i && i.label).length,
    [every, options],
  )
  const filtering =
    searchable || Boolean(searchFirst) || panelRows > SCROLLS_PAST
  const q = fold(query)
  const waiting = Boolean(searchFirst) && q === ''
  const shown = useMemo(
    () =>
      waiting
        ? all.filter((o) => o.pinned || o.value === value)
        : filtering && q
          ? all.filter(
              (o) =>
                o.pinned ||
                /* The description too: it is where an option says what
                 distinguishes it from the one above — which fund it belongs
                 to, which part it sits under — and a filter blind to that
                 answers "no such thing" to somebody typing the only word they
                 know it by. */
                fold(
                  `${o.label} ${o.hint ?? ''} ${o.note ?? ''} ${o.description ?? ''} ${o.trail ?? ''}`,
                ).includes(q),
            )
          : all,
    [all, filtering, q, waiting, value],
  )

  const chosen = values ?? []
  const pick = (v: T) => {
    if (multiple) {
      onValuesChange?.(
        chosen.includes(v) ? chosen.filter((c) => c !== v) : [...chosen, v],
      )
      return
    }
    onValueChange?.(v)
  }

  const {
    activeIndex,
    setActiveIndex,
    openAt,
    close,
    onKeyDown,
    onSearchKeyDown,
  } = useListbox({
    items: shown,
    value: multiple ? null : (value ?? null),
    closeOnPick: !multiple,
    onPick: (v) => pick(v as T),
    search: filtering ? q : '',
    open,
    onOpenChange: (next) => {
      setOpen(next)
      if (!next) setQuery('')
    },
  })

  // Keep the active option in view when the keyboard moves past the fold —
  // otherwise arrowing down a long list walks off the bottom of the panel and
  // the cursor is somewhere you cannot see.
  const activeRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // activeIndex is read through the ref's identity rather than in the body,
    // so it is named here as the trigger it is.
    if (activeIndex >= 0)
      activeRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const selected = all.find((o) => o.value === value) ?? null
  const TriggerIcon = (!multiple && selected?.icon) || Icon
  const triggerCls =
    variant === 'icon'
      ? 'select-icon__trigger'
      : variant === 'tool'
        ? 'tool select-tool__trigger'
        : 'control select__trigger'

  /** Render groups as headings; options carry their index in the FLAT list. */
  let flatIndex = -1
  const searching = filtering && q !== ''
  /* Depth is drawn in three steps, and anything deeper sits at the third. The
     trail says the whole path in search results and to a screen reader; the
     eye has only the indent. */
  const depthOf = (o: SelectOption<T>) =>
    !searching && o.depth ? String(Math.min(o.depth, 3)) : undefined
  const body = (searching ? [{ label: '', options: shown }] : options).map(
    (item, gi) => {
      const group = 'options' in item ? item : { label: '', options: [item] }
      const rows = group.options
        .filter((o) => (o.heading ? !searching && !waiting : shown.includes(o)))
        .map((o) => {
          if (o.heading) {
            return (
              <p
                key={o.value}
                className="select__branch"
                data-depth={depthOf(o)}
                /* Not an option: the keyboard never lands here, and a screen
                   reader hears the options beneath it by their trail. */
                aria-hidden
              >
                {o.label}
              </p>
            )
          }
          flatIndex += 1
          const i = flatIndex
          const isSelected = multiple
            ? chosen.includes(o.value)
            : o.value === value
          return (
            <div
              /* A listbox option is a div by spec. A <button> here is announced
                 as a button inside a listbox, which is the wrong role for what
                 it is; the rule is configured off in biome.json for exactly
                 this controlled pattern. */
              role="option"
              key={o.value}
              id={optionId(i)}
              /* -1, deliberately: focus stays on the TRIGGER and
               `aria-activedescendant` names the active option. Tabbable options
               would put every choice in the tab order, so leaving a select with
               forty options would cost forty presses. */
              tabIndex={-1}
              ref={i === activeIndex ? activeRef : undefined}
              aria-selected={isSelected}
              aria-disabled={o.disabled || undefined}
              className={
                variant === 'field' ? 'select__opt' : 'select-tool__opt'
              }
              data-selected={isSelected ? '' : undefined}
              data-active={i === activeIndex ? '' : undefined}
              data-disabled={o.disabled ? '' : undefined}
              data-depth={depthOf(o)}
              /* A nested row's place is drawn, not written, while browsing; a
                 screen reader is told it in words. */
              aria-label={
                !searching && o.trail
                  ? [o.label, o.trail, o.hint, o.note, o.description]
                      .filter(Boolean)
                      .join(', ')
                  : undefined
              }
              onMouseEnter={() => !o.disabled && setActiveIndex(i)}
              onKeyDown={onKeyDown}
              onClick={() => {
                if (o.disabled) return
                pick(o.value)
                if (!multiple) close()
              }}
            >
              <span>
                {o.icon ? (
                  <o.icon size={14} className="select__icon" aria-hidden />
                ) : null}
                {o.label}
                {searching && o.trail ? (
                  <span className="select__trail"> · {o.trail}</span>
                ) : null}
              </span>
              {o.hint ? <span className="select__hint">{o.hint}</span> : null}
              {o.note ? (
                <span className="select__hint" data-words="">
                  {o.note}
                </span>
              ) : null}
              {isSelected ? <Check size={14} aria-hidden /> : null}
              {/* Below the row rather than in it, so the label keeps the width
                  it needs and the sentence takes as many lines as it takes. */}
              {o.description ? (
                <p className="select__desc">{o.description}</p>
              ) : null}
            </div>
          )
        })
      if (rows.length === 0) return null
      return (
        <div className="select__group" key={group.label || gi}>
          {group.label ? (
            <p className="select__grouplabel">{group.label}</p>
          ) : null}
          {rows}
        </div>
      )
    },
  )

  /*
   * An icon trigger is named by a Tooltip, and the component draws it rather
   * than the caller.
   *
   * Every icon-only control in this system owes one, and a rule each call site
   * has to remember is a rule the fourth call site breaks. It goes quiet while
   * the list is open, for the reason `Tooltip` states: the pointer is still on
   * the trigger, and the name would sit over the panel's first option.
   */
  const named = (trigger: ReactNode) =>
    variant === 'icon' && label ? (
      <Tooltip label={label} quiet={open}>
        {trigger}
      </Tooltip>
    ) : (
      trigger
    )

  return (
    <div
      className={variant === 'tool' ? 'select-tool' : 'select'}
      data-bilingual={bilingual ? '' : undefined}
      ref={ref}
    >
      {named(
        <button
          ref={triggerRef}
          type="button"
          id={id}
          className={triggerCls}
          disabled={disabled}
          data-active={open ? '' : undefined}
          data-invalid={refused ? '' : undefined}
          data-placeholder={
            (multiple ? chosen.length === 0 : !selected) ? '' : undefined
          }
          /* combobox, not button. `aria-activedescendant` is not supported on
           role=button, so a button trigger announced the widget correctly and
           then told a screen reader nothing about which option the arrow keys
           were on. This is the WAI-ARIA select-only combobox pattern. */
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={
            open && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          /* An icon trigger has no text content, so its name has to carry the
           ANSWER as well. Every other variant states the value in the trigger
           where a reader can hear it; without this one a screen reader gets
           "Text direction, combobox" and the current answer is discoverable
           only by opening the list. */
          aria-label={
            variant === 'icon' && selected
              ? `${label}: ${selected.label}`
              : label
          }
          aria-invalid={refused || undefined}
          aria-describedby={describedBy}
          onKeyDown={onKeyDown}
          onClick={() => (open ? close() : openAt())}
        >
          {TriggerIcon ? (
            <TriggerIcon size={variant === 'icon' ? 15 : 16} aria-hidden />
          ) : null}
          {/* An icon trigger carries neither the word nor the chevron. It sits in
            a band of icon tools, where a framed control wearing a label reads
            as a different kind of thing — and the answer is said by the glyph,
            the chosen option's own where it has one, and by the tick in the
            open list.
            The name is on `aria-label`, and a Tooltip gives it to the eye. */}
          {variant === 'icon' ? null : (
            <>
              <span
                className={variant === 'tool' ? 'tool__label' : 'select__value'}
              >
                {multiple
                  ? (summary ?? placeholder)
                  : (selected?.label ?? placeholder)}
              </span>
              {/* A field fills its cell whatever it holds, so the words that
                  say what the answer means can travel with it into the box. */}
              {variant === 'field' && !multiple && selected?.note ? (
                <span className="select__hint" data-words="">
                  {selected.note}
                </span>
              ) : null}
              <ChevronDown size={14} aria-hidden />
            </>
          )}
        </button>,
      )}
      {/* The platform still submits the value, so a form that read the native
          element keeps working without every caller growing state for it. */}
      {name ? (
        <input
          type="hidden"
          name={name}
          value={multiple ? chosen.join(',') : (value ?? '')}
        />
      ) : null}
      {open
        ? createPortal(
            <div
              className={
                variant === 'field' ? 'select__pop' : 'select-tool__pop'
              }
              ref={panelRef}
              data-bilingual={bilingual ? '' : undefined}
              style={
                maxHeight
                  ? ({ '--popover-max-h': maxHeight } as CSSProperties)
                  : undefined
              }
            >
              {filtering ? (
                <div className="select-tool__search">
                  <Search size={14} aria-hidden />
                  <input
                    className="select-tool__search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onSearchKeyDown}
                    placeholder="Search…"
                    aria-label={label ? `Search ${label}` : 'Search options'}
                    /* The panel opened because somebody reached for this field, so
                   focus is what they asked for. */
                    autoFocus
                  />
                </div>
              ) : null}
              {/* The listbox is the scrolling list, not the frame around it: a
              textbox is not an option, so a search field inside `role="listbox"`
              describes the widget wrongly to a screen reader. */}
              <div
                className="select__list"
                id={listId}
                role="listbox"
                aria-multiselectable={multiple || undefined}
                aria-label={label ?? placeholder}
              >
                {waiting ? (
                  <p className="select-tool__empty">{searchFirst}</p>
                ) : null}
                {body}
                {shown.length === 0 && !waiting ? (
                  <p className="select-tool__empty">No matches</p>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}
